import { DownloadRecordModal } from '@src/modules/Records/shared/ViewRecordModal/components/DownloadRecordModal';
import {
  Button,
  ButtonGroup,
  Constants,
  Icon,
  Image,

  LightboxContextGroup,
  Loading,
  Tabs,
  Tooltip,
  toastManager,
  useContainerDimensionsContext } from
'@therms/atalaya';
import {
  Alerts,
  FilesListDisplay,
  IdLabel,
  RPCDocs,
  RPCs,
  Tags,
  UserClientViewingRecord,
  type DownloadPdfResult,
  useCurrentUserAnPCheck,
  useDownloadsApiServiceContext,
  useRPC,
  useRPCServerEventHandler,
  useTabsState } from
'@therms/react-modules';
import { downloadBase64AsPdf } from '@therms/web-js';
import classNames from 'classnames';
import { useState } from 'react';
import { IoMdDownload } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { MdRefresh, MdTimer } from 'react-icons/md';
import { CommentsTab } from './components/CommentsTab';
import { InfoTab } from './components/InfoTab';
import { TagsTab } from './components/TagsTab';
import { ViewRefLinks } from './ViewRefLinks';

interface ViewRecordProps {
  closeHandler: () => void;
  recordId: string;
}

function ViewRecord({ closeHandler, recordId }: ViewRecordProps) {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [tab, setTab] = useTabsState(`record-tab`, `information`);
  const {
    viewport: { width: viewportWidth }
  } = useContainerDimensionsContext();
  const canViewComments = useCurrentUserAnPCheck(
    (user) => user.anp.role.portal.access.viewRecordComments
  );
  const {client} = useDownloadsApiServiceContext()

  const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width;

  const { call, data, isCalling, success } = useRPC<
    RPCDocs.Records.GetRecordProfile.v1.Args,
    RPCDocs.Records.GetRecordProfile.v1.Data>(
    RPCs.Records.GetRecordProfile.v1, {
      recordId
    });

  const downloadRecord = (withComments) => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      { autoClose: false, position: 'top-center' }
    );

    client.records.DownloadRecord({body: {recordId, withComments}})
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `Record ${data?.record.type} - ${data?.record.title}`,
            result.pdfBase64,
          )
        }
      })
      .catch((err: any) => {
        console.error('err downloading entry', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }

  const handleDownloadPDF = () => {
    if (canViewComments) {
      setIsDownloadModalOpen(true);
    } else downloadRecord(false);
  };

  useRPCServerEventHandler('RecordUpdated', (evt, payload) => {
    if (payload.recordId === recordId) call();
  });

  if (!isCalling && !success)
  return (
    <div className="inline-flex justify-center items-center gap-x-xs w-full text-color-critical">






        <span>There was a problem loading</span>{' '}
        <Button onClick={call}>Retry</Button>
      </div>);


  if (!data) return <Loading overlay size="xxl" />;

  const { record } = data;

  return (
    <LightboxContextGroup files={record.fileUrls}>
      <UserClientViewingRecord recordId={record.id} />

      <div className={classNames({ 'pt-xs flex-col': isMobileLayout }, 'flex')}>
        <div
          className={classNames(
            {
              '-top-xl z-sticky pr-base pt-sm sticky w-64 shrink-0 self-start':
              !isMobileLayout
            },
            'flex flex-col'
          )}>

          <div className="relative flex shrink-0 flex-col">
            {record.primaryImageUrl &&
            <Image
              className={classNames({
                'min-h-[250px] max-w-[250px]': !isMobileLayout,
                'mx-auto max-h-[250px] min-w-[250px]': isMobileLayout
              })}
              fit="cover"
              fluid
              lightbox
              rounded
              src={record.primaryImageUrl} />

            }

            {isMobileLayout &&
            <div className="bg-surface absolute right-0 top-0 rounded-bl">
                <ButtonGroup subtle size="sm">
                  <Button onClick={handleDownloadPDF} variant="main">
                    <Icon Icon={IoMdDownload} />
                  </Button>

                  <Button onClick={closeHandler} subtle size="sm">
                    <Icon Icon={IoClose} />
                  </Button>
                </ButtonGroup>
              </div>
            }
          </div>

          <div>
            <div className="mt-sm text-color-subtle text-sm font-medium capitalize">
              {record.type}
            </div>

            <div className="text-lg font-medium">
              {record.title}{' '}
              <IdLabel
                className="text-color-subtle text-base font-bold tracking-wide"
                idLabel={record.idLabel} />
            </div>
          </div>

          <div className={classNames(isMobileLayout ? 'mt-sm' : 'mt-lg')}>
            <Tags tags={record.tags} />
          </div>
        </div>

        <div
          className={classNames(
            { 'border-border border-l': !isMobileLayout },
            'flex grow flex-col'
          )}>
          <div
            className={classNames(
              {
                '-top-xl z-sticky space-x-sm bg-surface sticky flex items-start':
                !isMobileLayout
              },
              'pt-sm'
            )}>

            <Tabs
              initialTabKey={tab || 'information'}
              currentKey={tab}
              onTabClick={setTab}>

              <Tabs.Tab title="Info" tabKey="information" />
              <Tabs.Tab title="Ref Links" tabKey="refs" />
              <Tabs.Tab
                tabKey="tags"
                title={
                <div className="space-x-xxs flex items-center">
                    <span>Tags</span>
                    {!!data.record.dateSensitiveTags?.length &&
                  <Icon Icon={MdTimer} size="xs" />
                  }
                  </div>
                } />


              {!!canViewComments &&
              <Tabs.Tab tabKey="comments" title="Comments" />
              }

              <Tabs.Tab
                notifications={record.fileUrls.length}
                tabKey="files"
                title="Files" />

            </Tabs>

            {!isMobileLayout &&
            <ButtonGroup size="sm" subtle>
                <Button onClick={handleDownloadPDF} variant="main">
                  <Tooltip
                  tooltip={
                  <div className="text-color font-normal">
                        Download as PDF
                      </div>
                  }>

                    <Icon Icon={IoMdDownload} />
                  </Tooltip>
                </Button>

                <Button onClick={closeHandler}>
                  <Icon Icon={IoClose} />
                </Button>
              </ButtonGroup>
            }
          </div>

          <div className={classNames({ 'pl-base': !isMobileLayout }, 'grow')}>
            {tab === 'information' && <InfoTab record={record} />}

            {tab === 'refs' &&
            <ViewRefLinks
              forRef={{
                type: 'record',
                value: record.id
              }} />

            }

            {tab === 'tags' && <TagsTab record={data.record} />}

            {!!canViewComments && tab === 'comments' &&
            <CommentsTab recordId={record.id} />
            }

            {tab === 'files' &&
            <FilesListDisplay fileUrls={record?.fileUrls} />
            }
          </div>
        </div>
      </div>

      {isDownloadModalOpen &&
      <DownloadRecordModal
        onDownload={(showComments) => {
          downloadRecord(showComments);
          setIsDownloadModalOpen(false);
        }}
        closeHandler={() => setIsDownloadModalOpen(false)} />

      }
    </LightboxContextGroup>);

}

// used for code-splitting
// eslint-disable-next-line import-x/no-default-export
export default ViewRecord;
export { ViewRecord };